import { useState } from "react";

import { emailIsValid } from "utils";
import { formatQuotingUnitOfMeasure } from "utils/quotes";

import { DirectBookingAssigneeSelector } from "./DirectBookingAssigneeSelector";
import {
  buildAssignmentFromEmail,
  buildAssignmentFromSelection,
  createAssigneeOptions,
  getAssigneePriceError,
  getAssigneePriceHelperText,
  getAssigneeSelectionItem,
  getAssigneeStatus,
} from "./utils";

import type { DirectBookingAssignmentFormState } from "types/shipment";

import type { DirectBookingAssigneeCheck } from "../../../../utils";
import type { NetworkMember, PartnerSelectionItem } from "../../types";

interface DirectBookingAssigneeSelectorContainerProps {
  networkMembers: NetworkMember[];
  loadingNetwork: boolean;
  assignment: DirectBookingAssignmentFormState;
  onAssignmentChange: (assignment: DirectBookingAssignmentFormState) => void;
  assigneeCheck: DirectBookingAssigneeCheck;
  // Set once the shipper presses Review: the carrier and price then show their
  // errors inline.
  reviewAttempted: boolean;
  quotingUnitOfMeasure: UnitOfMeasureType;
  isProject: boolean;
}

export function DirectBookingAssigneeSelectorContainer(
  props: DirectBookingAssigneeSelectorContainerProps,
) {
  const {
    networkMembers,
    loadingNetwork,
    assignment,
    onAssignmentChange,
    assigneeCheck,
    reviewAttempted,
    quotingUnitOfMeasure,
    isProject,
  } = props;

  const [inputValue, setInputValue] = useState("");
  const [inputError, setInputError] = useState<string | null>(null);

  const handleInputChange = (value: string) => {
    setInputValue(value);
    setInputError(null);
  };

  const showInvalidEmailError = () => {
    setInputError(`Invalid email: ${inputValue.trim()}`);
  };

  const commitTypedEmail = () => {
    if (inputValue.trim() === "") {
      return;
    }
    const next = buildAssignmentFromEmail({
      email: inputValue,
      networkMembers,
      current: assignment,
    });
    if (next === null) {
      showInvalidEmailError();
      return;
    }
    onAssignmentChange(next);
    setInputValue("");
    setInputError(null);
  };

  /*
  A typed email commits on Enter or comma. Any other text on Enter is left to the
  autocomplete, which picks the highlighted network carrier, or hands the text to
  the selection handler, which flags it.
  */
  const handleKeyDown = (event: React.KeyboardEvent) => {
    const typedEmail = emailIsValid(inputValue.trim());
    if (event.key === "," || (event.key === "Enter" && typedEmail)) {
      event.preventDefault();
      event.stopPropagation();
      commitTypedEmail();
    }
  };

  const handleSelectionChange = (
    selections: (string | PartnerSelectionItem)[],
  ) => {
    const next = buildAssignmentFromSelection({
      selections,
      networkMembers,
      current: assignment,
    });
    if (next === null) {
      showInvalidEmailError();
      return;
    }
    onAssignmentChange(next);
    setInputError(null);
  };

  return (
    <DirectBookingAssigneeSelector
      selectedItem={getAssigneeSelectionItem(assignment, networkMembers)}
      options={createAssigneeOptions(networkMembers)}
      networkMembers={networkMembers}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onSelectionChange={handleSelectionChange}
      onKeyDown={handleKeyDown}
      onBlur={commitTypedEmail}
      inputError={inputError}
      assigneeStatus={getAssigneeStatus(
        assignment,
        assigneeCheck,
        reviewAttempted,
      )}
      assignment={assignment}
      onPriceChange={(price) => onAssignmentChange({ ...assignment, price })}
      onCurrencyChange={(currency) =>
        onAssignmentChange({ ...assignment, currency })
      }
      priceLabel={formatQuotingUnitOfMeasure(quotingUnitOfMeasure)}
      priceHelperText={getAssigneePriceHelperText(isProject)}
      priceError={getAssigneePriceError(assignment, reviewAttempted)}
      loadingNetwork={loadingNetwork}
    />
  );
}
